import { useState } from 'react';
import { GRID, INK, INK_MUTED, SURFACE, TooltipBox, compactNumber, niceMax, useElementWidth } from './common';

/**
 * Linha única (evolução mensal da rede). Traço 2px, área a ~10%, marcador
 * ≥ 8px com anel de superfície no último ponto e no ponto em foco. Crosshair:
 * o ponteiro em qualquer lugar acha o X mais próximo.
 */
export function LineChart({
  labels,
  values,
  color,
  formatLabel,
  formatValue,
  height = 180,
  ariaLabel
}: {
  labels: string[];
  values: number[];
  color: string;
  formatLabel: (l: string) => string;
  formatValue: (v: number) => string;
  height?: number;
  ariaLabel: string;
}) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const n = values.length;
  if (n === 0) return null;

  const padL = 36;
  const padR = 12;
  const padT = 10;
  const padB = 22;
  const plotW = Math.max(width - padL - padR, 10);
  const plotH = height - padT - padB;
  const vmax = niceMax(Math.max(...values));
  const x = (i: number) => padL + (n === 1 ? plotW / 2 : (plotW * i) / (n - 1));
  const y = (v: number) => padT + plotH * (1 - v / vmax);
  const pts = values.map((v, i) => `${x(i)},${y(v)}`).join(' ');
  const area = `M${x(0)},${y(0)} L${pts.replace(/ /g, ' L')} L${x(n - 1)},${padT + plotH} L${x(0)},${padT + plotH} Z`;
  const last = n - 1;
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(Math.floor(plotW / 48), 1)));

  function pick(clientX: number, rect: DOMRect) {
    const rel = clientX - rect.left - padL;
    const i = n === 1 ? 0 : Math.round((rel / plotW) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  }

  return (
    <div ref={ref} className="relative">
      {hover !== null && (
        <TooltipBox
          title={formatLabel(labels[hover]!)}
          width={width}
          x={x(hover)}
          rows={[{ label: ariaLabel, value: formatValue(values[hover]!), color }]}
        />
      )}
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={ariaLabel}
        tabIndex={0}
        style={{ display: 'block', overflow: 'visible', outline: 'none' }}
        onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
        onPointerDown={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
        onPointerLeave={() => setHover(null)}
        onFocus={() => setHover(last)}
        onBlur={() => setHover(null)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') setHover((h) => Math.max(0, (h ?? last) - 1));
          if (e.key === 'ArrowRight') setHover((h) => Math.min(last, (h ?? last) + 1));
        }}
      >
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={padL} x2={width - padR} y1={y(vmax * f)} y2={y(vmax * f)} stroke={GRID} strokeWidth={1} />
            <text x={padL - 6} y={y(vmax * f) + 3} textAnchor="end" fontSize={10} fill={INK_MUTED}>
              {compactNumber(vmax * f)}
            </text>
          </g>
        ))}
        <path d={area} fill={color} opacity={0.1} />
        <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null && (
          <line x1={x(hover)} x2={x(hover)} y1={padT} y2={padT + plotH} stroke={INK} strokeOpacity={0.25} strokeWidth={1} />
        )}
        {[hover ?? last].map((i) => (
          <circle key={i} cx={x(i)} cy={y(values[i]!)} r={4.5} fill={color} stroke={SURFACE} strokeWidth={2} />
        ))}
        {labels.map((l, i) =>
          i % labelEvery === 0 || i === last ? (
            <text key={l} x={x(i)} y={height - 6} textAnchor="middle" fontSize={10} fill={INK_MUTED}>
              {formatLabel(l)}
            </text>
          ) : null
        )}
      </svg>
    </div>
  );
}
